/** @fileoverview Shared constants for get_index_worker_export_data. */

/** Tool name (single source of truth). */
export const GET_INDEX_WORKER_EXPORT_DATA_NAME =
  "get_index_worker_export_data" as const;

/** GUI-equivalent lower bound for cutoff-style IW exports. */
export const INDEX_WORKER_EXPORT_DEFAULT_RANGE_START =
  "1970-01-01T00:00:00Z" as const;

/** Description shown to the LLM. */
export const GET_INDEX_WORKER_EXPORT_DATA_DESC = `Prepares Index Worker export parameters for GUI-equivalent downloads.

Use this tool when the user asks to export Index Worker data as a downloadable file.
Return export parameters only; do not return large tabular rows in chat.

Input:
- 'indexWorkerId' (required): GraphQL global ID of the Index Worker. Do not pass a Firestore document ID suffix (for example, "Cqy72yDlHymP0AtewCUt").
- 'exportTarget' (required): 'trends' or 'urls'.
- 'format' (required): when exportTarget='trends', use only 'csv'; when exportTarget='urls', use 'csv' | 'excel' | 'json'.
- Optional 'dateRange' ({ end } or { start, end } in ISO-8601 UTC "YYYY-MM-DDTHH:mm:ssZ"). If 'start' is omitted, it defaults to '${INDEX_WORKER_EXPORT_DEFAULT_RANGE_START}'.
- Optional comparison: pass both 'isComparing' and 'comparingDateRange'. If one is omitted, the tool infers comparison intent and still prepares comparison export parameters. 'comparingDateRange' also accepts { end } only, and omitted 'start' defaults to '${INDEX_WORKER_EXPORT_DEFAULT_RANGE_START}'. When 'isComparing' is true and 'comparingDateRange' is omitted, it falls back to 'dateRange'.

Usage rules:
- Always call with explicit exportTarget/format. Use exportTarget='trends' for chart CSV, and exportTarget='urls' for URL table CSV/Excel/JSON.
- ID resolution:
  - If the current URL includes '/iw/{projectId}/{indexWorkerId}', use that '{indexWorkerId}' segment (URL-decoded) as-is.
  - If only a short ID or a worker name is known, call 'list_index_workers' with projectId and use the returned 'id'.
- For trend period interpretation, prioritize IW detail GUI semantics:
  - 'latest' => no dateRange.
  - '7 days ago'/'28 days ago'/'3 months ago'/'6 months ago'/'12 months ago'/'16 months ago'
    => cutoff ranges from ${INDEX_WORKER_EXPORT_DEFAULT_RANGE_START} to (today minus that period) at 23:59:59Z.
  - If the user explicitly asks for an upper-bound cutoff (e.g., "until 3 months ago", "before 3 months ago"), always use the cutoff range above.
  - Use a recent range from (today minus that period) to today only when the user explicitly asks for a recent window (e.g., "last 3 months", "recent 3 months").
  - If phrasing is ambiguous (e.g., "3 months worth"), prefer GUI-equivalent cutoff interpretation.

Returns:
- context { targetType, targetId, indexWorkerName, exportTarget, format, dateRange?, isComparing?, comparingDateRange? }
- exportRequest { indexWorkerId, indexWorkerName, exportTarget, format, dateRange, isComparing, comparingDateRange }`;
